import React, { useState } from 'react';
import '../../../css/ViewCouponModal.css';
import ConfirmationModal from './ConfirmationModal';
// ✅ 1. Import your custom api instance instead of axios
import api from '../../../api/axiosConfig';
import { showToast } from '../../Shared/Toaster';


interface Props {
  coupon: any;
  onClose: () => void;
  onUpdate: () => void;
  onEdit: () => void;
}

const ViewCouponModal: React.FC<Props> = ({ coupon, onClose, onUpdate, onEdit }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-IN');
  };

  const handleEditClick = () => {
    onClose(); // Close the view modal
    onEdit(); // Trigger edit modal opening
  };

  // ✅ 2. Updated the handleDisable function
  const handleDisable = async () => {
    setIsDisabling(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) {
        showToast("Authentication error: You are not logged in.", 'error');
        setIsDisabling(false);
        return;
      }

      const payload = {
        is_active: false,
        disabled_at: new Date().toISOString()
      };
      
      await api.payment_api.put(
        `/admin/promocodes/${coupon.promo_code_id}`,
        payload,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
      );
      
      showToast("Coupon disabled successfully.", 'success');
      onUpdate();
      onClose();
    } catch (error: any) {
      console.error('Error disabling coupon:', error);
      const errorMessage = error.response?.data?.message || 'Failed to disable coupon.';
      showToast(`Error: ${errorMessage}`, 'error');
    } finally {
      setIsDisabling(false);
      setShowConfirmation(false);
    }
  };

  return (
    <>
      <div className="view-coupon-modal-overlay" onClick={onClose}>
        <div className="view-coupon-modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>View Coupon Code</h2>
            <button className="global-close-btn" onClick={onClose}>✕</button>
          </div>

          <div className="view-coupon-field">
            <label>Coupon Code</label>
            <div className="value bold">{coupon.code}</div>
          </div>

          <div className="view-coupon-field">
            <label>Code Type</label>
            <div className="view-coupon-value">
              {coupon.code_type?.toLowerCase() === 'promo' && <span className="view-coupon-badge purple">Promo code</span>}
              {coupon.code_type?.toLowerCase() === 'venue' && <span className="view-coupon-badge blue">Venue code</span>}
            </div>
          </div>

          <div className="view-coupon-row">
            <div>
              <label>Created On</label>
              <div className="view-coupon-value">{formatDate(coupon.created_at)}</div>
            </div>
            <div>
              <label>Disabled On</label>
              <div className="view-coupon-value">{formatDate(coupon.disabled_at || coupon.valid_until)}</div>
            </div>
          </div>

          <div className="view-coupon-card">
            <label>Validity Period</label>
            <div className="view-coupon-row">
              <div>
                <label>Start Date</label>
                <div className="view-coupon-value">{formatDate(coupon.valid_from)}</div>
              </div>
              <div>
                <label>End Date</label>
                <div className="view-coupon-value">{formatDate(coupon.valid_until)}</div>
              </div>
            </div>
          </div>

          <div className="view-coupon-card">
            <label>Fee Discount</label>
            <div className="view-coupon-value">₹{coupon.fee_discount}</div>
          </div>

          <div className="view-coupon-field">
            <label>Redemption Limit</label>
            <div className="view-coupon-value">
              {coupon.redemption_limit || 'Unlimited'}
              {coupon.redemption_limit && ` (${coupon.current_redemption_count || 0} used)`}
            </div>
          </div>

          <div className="view-coupon-actions">
            <button className="view-coupon-close-btn" onClick={onClose}>Close</button>
            <button className="view-coupon-edit-btn" onClick={handleEditClick}>Edit</button>
            <button 
              className="view-coupon-disable-btn" 
              onClick={() => setShowConfirmation(true)}
              disabled={isDisabling || !coupon.is_active}
            >
              {isDisabling ? 'Disabling...' : 'Disable'}
            </button>
          </div>
        </div>
      </div>

      {showConfirmation && (
        <ConfirmationModal
          message="Are you sure you want to disable this coupon?"
          onConfirm={handleDisable}
          onCancel={() => setShowConfirmation(false)}
        />
      )}
    </>
  );
};

export default ViewCouponModal;